import {
  isValidPhoneNumber,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input";

/**
 * Default markets where mobile money collection is supported. Payment pages
 * carry their own `markets` list from the API, which takes precedence.
 */
export const MOBILE_MONEY_COUNTRIES: readonly Country[] = ["TZ", "KE", "UG"];

export const MOBILE_MONEY_PHONE_ERROR =
  "Use a Tanzanian, Kenyan or Ugandan number for mobile payment";

export const INVALID_PHONE_ERROR = "Enter a valid phone number";

/** ISO country of an E.164 number, or null when it cannot be parsed. */
export function phoneCountry(phone: string): Country | null {
  try {
    return parsePhoneNumber(phone)?.country ?? null;
  } catch {
    return null;
  }
}

/** True when the E.164 number belongs to one of the given mobile-money markets. */
export function isMobileMoneyPhone(
  phone: string,
  countries: readonly Country[] = MOBILE_MONEY_COUNTRIES,
): boolean {
  const country = phoneCountry(phone);
  return !!country && countries.includes(country);
}

/**
 * Why a number cannot pay with mobile money yet, if anything:
 * - "unsupported": it clearly belongs to a market we do not collect in
 * - "incomplete": too short to tell, or not a valid number for its country
 * A number is flagged as unsupported only once its country is known, so a
 * half-typed number never shows the market error.
 */
export function phoneMarketIssue(
  phone: string,
  countries: readonly Country[] = MOBILE_MONEY_COUNTRIES,
): "unsupported" | "incomplete" | null {
  const country = phoneCountry(phone);
  if (!country) return "incomplete";
  if (!countries.includes(country)) return "unsupported";
  return isValidPhoneNumber(phone) ? null : "incomplete";
}
